import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { AdBanner } from "@/components/AdBanner";
import { articleBySlug, ARTICLES } from "@/data/articles";

export default function Article() {
  const { slug } = useParams();
  const article = articleBySlug(slug);

  // GEO: inject Article + FAQ structured data (JSON-LD) for AI engines & search.
  useEffect(() => {
    if (!article) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "Video Game Mania" },
      image: article.image,
      mainEntityOfPage: window.location.href,
    };
    const faq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (article.faqs || []).map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const s1 = document.createElement("script");
    s1.type = "application/ld+json"; s1.dataset.geo = "article"; s1.text = JSON.stringify(ld);
    const s2 = document.createElement("script");
    s2.type = "application/ld+json"; s2.dataset.geo = "faq"; s2.text = JSON.stringify(faq);
    document.head.appendChild(s1); document.head.appendChild(s2);
    return () => { s1.remove(); s2.remove(); };
  }, [article]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="font-display font-black text-3xl uppercase">Article not found</p>
        <Link to="/news" className="text-neon-cyan hover:underline mt-4 inline-block">← Back to news</Link>
      </div>
    );
  }

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="article-page">
      <Seo title={article.title} description={article.excerpt} />
      <Link to="/news" className="inline-flex items-center gap-1 text-zinc-400 hover:text-neon-cyan text-sm font-semibold uppercase tracking-widest mb-6">
        <ArrowLeft className="w-4 h-4" /> Gaming News
      </Link>

      <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-neon-cyan text-black rounded mb-4">{article.category}</span>
      <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tighter leading-[1.05]">{article.title}</h1>
      <div className="flex items-center gap-3 mt-4 text-sm text-zinc-500 font-mono">
        <span>{article.author}</span><span>·</span>
        <span>{new Date(article.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.readTime}</span>
      </div>

      <img src={article.image} alt={article.title} className="w-full aspect-video object-cover rounded-2xl mt-6 border-2 border-zinc-800" />

      {article.keyTakeaways && (
        <div className="mt-8 rounded-xl border-2 border-neon-lime/40 bg-neon-lime/5 p-5">
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-neon-lime mb-3">Key Takeaways</h2>
          <ul className="space-y-2">
            {article.keyTakeaways.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-zinc-200 text-sm">
                <Check className="w-4 h-4 text-neon-lime shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      <article className="mt-8 space-y-8">
        {article.sections.map((sec, i) => (
          <section key={i}>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-3 text-white">{sec.heading}</h2>
            {sec.body?.map((p, j) => <p key={j} className="text-zinc-300 leading-relaxed mb-3">{p}</p>)}
            {sec.list && (
              <ul className="space-y-2 mt-2">
                {sec.list.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-zinc-300">
                    <span className="text-neon-cyan mt-1.5 w-1.5 h-1.5 rounded-full bg-neon-cyan shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>

      <AdBanner slot="article" className="my-10" />

      {article.faqs && (
        <section className="mt-4">
          <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {article.faqs.map((f, i) => (
              <div key={i} className="rounded-xl border-2 border-zinc-800 bg-zinc-950 p-5" data-testid={`faq-${i}`}>
                <h3 className="font-display font-bold text-lg mb-1">{f.q}</h3>
                <p className="text-zinc-400">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-12">
        <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400 mb-4">More Gaming News</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {related.map((a) => (
            <Link key={a.slug} to={`/news/${a.slug}`} className="group block">
              <img src={a.image} alt={a.title} loading="lazy" className="w-full aspect-video object-cover rounded-lg border-2 border-zinc-800 group-hover:border-neon-cyan transition-colors" />
              <h3 className="font-display font-bold text-sm uppercase tracking-tight mt-2 group-hover:text-neon-cyan transition-colors leading-tight">{a.title}</h3>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
